import React, { ReactNode } from 'react';
import { BaseContentCard } from './BaseContentCard/BaseContentCard';
import { TagProps } from './Tag/Tag';
import { Footer, FooterProps } from './Footer/Footer';
import { PossiblyResponsiveValue, useResponsiveValue } from '../hooks/useResponsiveValue';
import { styles } from './ContentCard.css';
import { LoadingSkeleton } from '../LoadingSkeleton/LoadingSkeleton';
import { Body } from './Body/Body';

type ContentCardHeadingSize = 'large' | 'medium' | 'small';

export type ContentCardProps = {
  cover: ReactNode;
  isContained?: boolean;
  isLoading?: boolean;
  showTag?: boolean;
  tagProps?: Omit<TagProps, 'className'>;
  footerProps?: string | FooterProps;
  heading?: string;
  headingSize?: ContentCardHeadingSize;
  content?: string | ReactNode;
  maxContentLines?: number;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  href?: string;
  width?: PossiblyResponsiveValue<React.CSSProperties['width']>;
};

export const ContentCard = ({
  cover,
  isContained = true,
  tagProps,
  footerProps,
  heading,
  headingSize = 'small',
  content,
  maxContentLines = 3,
  onClick,
  href,
  isLoading = false,
  showTag = false,
  width: widthProp,
}: ContentCardProps) => {
  const width = useResponsiveValue(widthProp);
  const isCoverOnly = !heading && !content && !footerProps;

  if (isLoading) {
    return <LoadingSkeleton.ContentCard width={width} height="100%" isContained={isContained} shouldShimmer />;
  }

  return (
    <BaseContentCard
      style={{ width: width ? `${width}` : undefined }}
      className={styles.container}
      href={href}
      cover={cover}
      onClick={onClick}
      footer={
        !footerProps ? null : typeof footerProps === 'string' ? (
          <Footer leftContent={footerProps} />
        ) : (
          <Footer {...footerProps} />
        )
      }
      isContained={isContained}
    >
      {!isCoverOnly ? (
        <Body
          headingSize={headingSize}
          heading={heading}
          content={content}
          showTag={showTag}
          tagProps={tagProps}
          maxContentLines={maxContentLines}
        />
      ) : null}
    </BaseContentCard>
  );
};
